"use client";
import { useState, useEffect } from "react";
import {
  Wallet,
  Send,
  Plus,
  Clock,
  Shield,
  Users,
  TrendingUp,
  Copy,
  ExternalLink,
  X,
  ArrowDownLeft,
  ArrowUpRight,
  TimerIcon,
  Nfc,
  CheckCircle,
  AlertCircle,
  ArrowRightCircle,
  Banknote,
  PlusIcon,
  UserCheck,
  LockIcon,
  HandCoins,
  Link2,
  FileText,
  Heart,
  Gift,
  Pencil,
  Check,
  Loader2,
} from "lucide-react";
import { GridLoader } from "react-spinners";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { updateUser } from "../slices/userSlices";
import { RootState } from "../store";
import { getBalance, getRecentTransactions } from "../services/authapi";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs, doc, getDoc, updateDoc, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useWallet } from "@crossmint/client-sdk-react-ui";
import { WebhookAlertBanner } from "./developers/webhooks/WebhookAlertBanner";

interface Transaction {
  id: string;
  type: "sent" | "received";
  amount: number;
  from: string;
  to: string;
  date: string;
  hash: string;
  senderEmail?: string | null;
  paymentMethod?: string | null;
  sourceLabel?: string | null;
  invoiceId?: string | null;
  // Collect-page fee fields. Populated only when the platform took a
  // fee (i.e. business-account Collect payments). `amount` is already
  // the seller's net (gross minus fee), so render `feeAmount` as a
  // separate small line — never subtract again.
  feeAmount?: number | null;
  feePct?: number | null;
  feeFlat?: number | null;
  grossAmount?: number | null;
  // Lifecycle / held-deposit context. `status` is one of:
  //   • "completed" — sweep landed on-chain (default state)
  //   • "held"      — invoice flagged (amount mismatch); funds are
  //                   stuck in the dedicated wallet pending manual
  //                   review. The row should be badged accordingly so
  //                   the seller doesn't mistake it for a real credit.
  //   • "pending"   — sweep didn't fire yet (rare; retried later)
  //   • "awaiting_approval" — Bridge bank deposit sitting in a CashTap
  //                   custody wallet until an admin approves the release.
  //   • "rejected"  — admin declined the custody deposit; funds were
  //                   never added to the user's balance.
  //   • "release_failed" — approved custody release that didn't land on
  //                   chain. Still in progress from the user's side, so
  //                   it renders like "awaiting_approval".
  // Legacy records pre-dating this field have `null` — treat as completed.
  status?:
    | "completed"
    | "held"
    | "pending"
    | "awaiting_approval"
    | "rejected"
    | "release_failed"
    | null;
  flagReason?: string | null;
  expectedAmount?: number | null;
  shortfall?: number | null;
}

interface ExternalAccount {
  id: string;
  bank_name: string;
  account_owner_name: string;
  account: {
    last_4: string;
    routing_number: string;
    checking_or_savings: string;
  };
  address: {
    street_line_1: string;
    city: string;
    state: string;
    postal_code: string;
  };
}

interface LiquidationAddress {
  id: string;
  address: string;
  state: string;
  created_at: string;
  destination_currency: string;
  destination_payment_rail: string;
}

interface Quote {
  id: string;
  status: string;
  developer_fee_percent: string;
  customer_id: string;
  created_at: string;
  source_deposit_instructions: {
    currency: string;
    bank_name: string;
    bank_address: string;
    bank_routing_number: string;
    bank_account_number: string;
    bank_beneficiary_name: string;
    bank_beneficiary_address: string;
    payment_rail: string;
    payment_rails: string[];
  };
  destination: {
    currency: string;
    payment_rail: string;
    address: string;
  };
}

const paymentLogos = [
  { src: "/applepay-logo.png", alt: "Apple Pay", scale: "scale-150 invert" },
  { src: "/googlepay-logo.png", alt: "Google Pay", scale: "scale-100" },
  { src: "/cashapp-logo.png", alt: "Cash App", scale: "scale-100" },
  { src: "/Stripe_Logo.png", alt: "Stripe", scale: "scale-100" },
  { src: "/mastercard-logo.png", alt: "Mastercard", scale: "scale-120" },
  { src: "/coinbase.svg", alt: "Coinbase", scale: "scale-120" },
  { src: "/crossmint-light.png", alt: "Crossmint", scale: "scale-120" },
  { src: "/bridge_logo.png", alt: "Bridge", scale: "scale-120" },
  { src: "/yield-logo.png", alt: "Yield", scale: "scale-120" },
  { src: "/applepay-logo.png", alt: "Apple Pay", scale: "scale-150 invert" },
  { src: "/googlepay-logo.png", alt: "Google Pay", scale: "scale-100" },
  { src: "/cashapp-logo.png", alt: "Cash App", scale: "scale-100" },
  { src: "/Stripe_Logo.png", alt: "Stripe", scale: "scale-100" },
  { src: "/mastercard-logo.png", alt: "Mastercard", scale: "scale-120" },
  { src: "/coinbase.svg", alt: "Coinbase", scale: "scale-120" },
  { src: "/crossmint-light.png", alt: "Crossmint", scale: "scale-120" },
  { src: "/bridge_logo.png", alt: "Bridge", scale: "scale-120" },
  { src: "/yield-logo.png", alt: "Yield", scale: "scale-120" },
];

type FieldProps = {
  label: string;
  value: string;
  isMonospace?: boolean;
  copyValue?: string;
};

export function Dashboard() {
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [kycState, setKycState] = useState<
    "not_started" | "incomplete" | "under_review" | "approved" | "rejected"
  >("not_started");
  const { wallet } = useWallet();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user) as any;
  const username = user?.username || "User";

  // CashTap Tag editing
  const [editingTag, setEditingTag] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [savingTag, setSavingTag] = useState(false);

  const saveTag = async () => {
    const clean = tagInput.trim().toLowerCase();
    if (!clean || clean.length < 3 || clean.length > 15 || /\s/.test(clean)) {
      toast.error("Username must be 3–15 characters, no spaces.");
      return;
    }
    if (clean === user?.username) { setEditingTag(false); return; }
    setSavingTag(true);
    try {
      const snap = await getDocs(query(collection(db, "users"), where("username", "==", clean)));
      if (!snap.empty) { toast.error("Username taken. Try another."); setSavingTag(false); return; }
      const uid = user?.uid || user?.wallet;
      if (!uid) throw new Error("No wallet");
      await updateDoc(doc(db, "users", uid), { username: clean });
      dispatch(updateUser({ username: clean }));
      toast.success("CashTap Tag updated!");
      setEditingTag(false);
    } catch {
      toast.error("Failed to update tag");
    } finally {
      setSavingTag(false);
    }
  };
  const walletAddress = (wallet?.address || user?.smartWallet || "").toLowerCase();
  const walletAddressWithoutLowerCase = (wallet?.address || user?.smartWallet || "")
  const adminWallet = import.meta.env.VITE_Admin_Wallet?.toLowerCase() || "";
  const serverUrl = import.meta.env.VITE_SERVERURL;
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingBalance, setLoadingBalance] = useState<boolean>(true);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(true);
  const [loadingProfiles, setLoadingProfiles] = useState<boolean>(true);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState<string>("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [showKycInfoModal, setShowKycInfoModal] = useState(false);
  // Bridge data
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [externalAccounts, setExternalAccounts] = useState<ExternalAccount[]>(
    [],
  );
  const [liquidationAddresses, setLiquidationAddresses] = useState<
    LiquidationAddress[]
  >([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingBridge, setLoadingBridge] = useState(true);
  // NEW: off-ramp status state

 
  const [showBankModal, setShowBankModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressToUsername, setAddressToUsername] = useState<{
    [address: string]: string | undefined;
  }>({});
  // NEW: store profile photos by wallet address
  const [addressToPhotoURL, setAddressToPhotoURL] = useState<{
    [address: string]: string | null;
  }>({});

  const isLoading =
    loadingBalance ||
    loadingTransactions ||
    loadingProfiles ||
    loadingBridge ||
    !user;

  function Field({ label, value, isMonospace = false, copyValue }: FieldProps) {
    const displayValue = value || "—";
    return (
      <div className="py-2.5 border-b border-gray-700/50 last:border-b-0">
        <p className="text-gray-400 text-sm mb-1">{label}</p>
        <div className="flex items-center justify-between gap-3">
          <p
            className={`text-white font-medium break-all ${
              isMonospace ? "font-mono text-sm" : ""
            }`}
          >
            {displayValue}
          </p>
          {(copyValue || value) && (
            <button
              onClick={() => copyToClipboard(copyValue || value)}
              className="p-1.5 hover:bg-gray-700 rounded-lg transition shrink-0"
              title="Copy"
            >
              <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Load Bridge status & data
  useEffect(() => {
    const loadBridgeData = async () => {
      if (!walletAddress) return;
      setLoadingBridge(true);
      try {
        const customerRef = doc(
          db,
          "bridge_customers-main",
          walletAddress.toLowerCase(),
        );
        const snap = await getDoc(customerRef);
        const cid = snap.exists() ? snap.data()?.customerId : null;
        setCustomerId(cid);
        if (cid) {
          const authH = { Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` };

          const eaRes = await fetch(
            `${serverUrl}/bridge/customers/${cid}/external_accounts`,
            { headers: authH },
          );
          const eaData = eaRes.ok ? await eaRes.json() : { data: [] };
          setExternalAccounts(eaData.data || []);

          const laRes = await fetch(
            `${serverUrl}/bridge/customers/${cid}/liquidation_addresses`,
            { headers: authH },
          );
          const laData = laRes.ok ? await laRes.json() : { data: [] };
          setLiquidationAddresses(laData.data || []);

          const qRes = await fetch(
            `${serverUrl}/bridge/customers/${cid}/virtual_accounts`,
            { headers: authH },
          );
          const qData = qRes.ok ? await qRes.json() : { data: [] };
          setQuotes(qData.data || []);

          const customerRes = await fetch(
            `${serverUrl}/bridge/customers/${cid}`,
            { headers: authH },
          );
          if (customerRes.ok) {
            const customer = await customerRes.json();
            setTermsAccepted(customer.has_accepted_terms_of_service);
            const baseEndorsement = customer.endorsements?.find(
              (e: any) => e.name === "base",
            );
            const isApproved =
              customer.status === "active" &&
              baseEndorsement?.status === "approved" &&
              !customer.requirements_due?.includes("id_verification");
            setKycState(
              customer.status === "rejected"
                ? "rejected"
                : customer.status === "under_review" ||
                    baseEndorsement?.status === "pending"
                  ? "under_review"
                  : isApproved
                    ? "approved"
                    : "incomplete",
            );
          }
        }
      } catch (err: any) {
        console.error("Failed to load Bridge data:", err);
        toast.error("Failed to load ramp status");
      } finally {
        setLoadingBridge(false);
      }
    };
    if (user) loadBridgeData();
  }, [walletAddress, user, serverUrl]);

  // Check for success redirect from Coinbase Offramp
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("success") === "offramp-redirect") {
      toast.success("Off-ramp initiated successfully!");
      window.history.replaceState({}, "", "/dashboard");
    }
  }, [location]);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!walletAddress) {
        toast.error("No wallet address found for the user.");
        setLoadingBalance(false);
        return;
      }
      try {
        const usdcBalance = await getBalance(walletAddressWithoutLowerCase);
        setWalletBalance(usdcBalance);
      } catch (err: any) {
        console.error("Error fetching USD balance:", err.message);
        toast.error("Failed to fetch wallet balance. Please try again.");
      } finally {
        setLoadingBalance(false);
      }
    };
    if (user) fetchBalance();
    else setLoadingBalance(false);
  }, [walletAddress, user]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!walletAddress) {
        toast.error("No wallet address found for the user.");
        setLoadingTransactions(false);
        return;
      }
      try {
        const txs = await getRecentTransactions(walletAddressWithoutLowerCase);
        const filteredTxs = txs.filter((tx: Transaction) => {
          if (tx.type === "sent") {
            return tx.to.toLowerCase() !== adminWallet;
          }
          return true;
        });
        setTransactions(filteredTxs);
      } catch (err: any) {
        console.error("Error fetching transactions:", err.message);
        toast.error("Failed to fetch transactions. Please try again.");
      } finally {
        setLoadingTransactions(false);
      }
    };
    if (user) fetchTransactions();
    else setLoadingTransactions(false);
  }, [walletAddress, user, adminWallet]);

  // Fetch usernames AND profile photos
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const vendorsSnap = await getDocs(collection(db, "vendors"));
        const nameMap: { [address: string]: string | undefined } = {};
        const photoMap: { [address: string]: string | null } = {};

        usersSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            const lower = data.wallet.toLowerCase();
            nameMap[lower] = data.username;
            photoMap[lower] = data.photoURL || null;
          }
        });

        vendorsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            const lower = data.wallet.toLowerCase();
            nameMap[lower] = data.name;
            photoMap[lower] = null; // vendors usually have no photo
          }
        });

        setAddressToUsername(nameMap);
        setAddressToPhotoURL(photoMap);
      } catch (err: any) {
        console.error("Error fetching profiles:", err.message);
        toast.error("Failed to fetch profiles. Please try again.");
      } finally {
        setLoadingProfiles(false);
      }
    };
    fetchProfiles();
  }, []);

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatAmount = (amount: number): string => {
    if (!isFinite(amount)) return "0";
    return parseFloat(amount.toFixed(2)).toString();
  };

  // Floored-to-2-decimals: never show more than what the seller actually
  // received on chain. e.g. 0.195 → "0.19", not "0.20".
  const formatNetAmount = (amount: number): string => {
    if (!isFinite(amount)) return "0.00";
    return (Math.floor(amount * 100) / 100).toFixed(2);
  };

  const formatFee = (amount: number): string => {
    if (!isFinite(amount)) return "0.00";
    return amount.toFixed(2);
  };

  const getCounterpartyDisplay = (tx: Transaction): string => {
    if (tx.type === "received" && tx.senderEmail) return tx.senderEmail;
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToUsername[counterparty] || "External";
  };

  const getCounterpartyPhoto = (tx: Transaction): string | null => {
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToPhotoURL[counterparty] || null;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const viewOnBlockExplorer = (hash: string) => {
    window.open(`https://basescan.org/tx/${hash}`, "_blank");
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }
    if (parseFloat(withdrawAmount) > parseFloat(walletBalance)) {
      toast.error("Amount exceeds available balance.");
      return;
    }
    setIsWithdrawing(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/create-offramp-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` },
          body: JSON.stringify({
            walletAddress: walletAddress.toLowerCase(),
            clientIp: "0.0.0.0",
          }),
        },
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const { sessionToken } = await response.json();
      if (!sessionToken) {
        throw new Error("No session token returned");
      }
      const offrampUrl =
        "https://pay.coinbase.com/v3/sell/input" +
        `?sessionToken=${sessionToken}` +
        `&partnerUserRef=${user?.uid}` +
        `&redirectUrl=${encodeURIComponent(
          "https://cashtap.cash/dashboard?success=offramp-redirect",
        )}`;
      const width = 480;
      const height = 720;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;
      window.open(
        offrampUrl,
        "coinbase-offramp",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes`,
      );
      toast.success("Opening Coinbase to complete withdrawal...");
      setShowWithdraw(false);
      setWithdrawAmount("");
    } catch (err: any) {
      toast.error(err.message || "Failed to initiate withdraw");
    } finally {
      setIsWithdrawing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <GridLoader color="#61CE70" size={20} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 min-h-screen px-4 sm:px-6 lg:px-8">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />

      {showKycInfoModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          onClick={() => setShowKycInfoModal(false)}
        >
          <div
            className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/60 rounded-3xl p-6 sm:p-8 w-full sm:max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowKycInfoModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
            <div className="text-center sm:text-left mt-2">
              <div className="flex items-center justify-center sm:justify-start gap-3 mb-5">
                <div className="p-3 bg-emerald-900/30 rounded-xl">
                  <Shield className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  Identity Verification
                </h3>
              </div>
              <p className="text-gray-300 text-base leading-relaxed mb-6">
                We verify your identity to:
              </p>
              <ul className="space-y-4 text-gray-300 text-left mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <span>Keep your account and funds secure.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <span>Prevent fraud and unauthorized access.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <span>
                    Enable your personal U.S. bank account for ACH & wire
                    transfers.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <span>Comply with U.S. banking regulations.</span>
                </li>
              </ul>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                This process is handled securely by CashTap’s regulated banking
                partners.
              </p>
              <button
                onClick={() => setShowKycInfoModal(false)}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-3.5 rounded-2xl transition mt-2"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {user?.isBusiness && (
        <WebhookAlertBanner wallet={walletAddress || null} email={user?.email || null} variant="home" />
      )}

      {/* Wallet Card */}
      <div className="w-auto mb-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 p-6 sm:p-8 lg:p-10 text-white shadow-2xl border border-emerald-800/50">
          <div className="absolute inset-0 bg-gradient-to-tr from-emerald-600/10 via-transparent to-transparent" />
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-teal-600/10 rounded-full blur-3xl" />
          <div className="absolute top-6 right-6 sm:top-8 sm:right-8">
            <div className="p-2.5 sm:p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-sm">
              <Nfc className="w-5 h-5 sm:w-6 sm:h-6 text-white/90" />
            </div>
          </div>
          <div className="absolute top-[6rem] left-6 sm:top-[8rem] sm:left-8 lg:top-[7.4rem] lg:left-10">
            <div className="w-12 h-8 sm:w-14 sm:h-9 lg:w-16 lg:h-10 bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 rounded-lg shadow-lg border border-amber-200/50 flex items-center justify-center">
              <div className="grid grid-cols-4 gap-0.5">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <div
                    key={i}
                    className="w-1.5 h-2.5 sm:w-2 sm:h-3 bg-amber-700/50 rounded-full"
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="relative z-10 flex items-center gap-4 sm:gap-5">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-emerald-400/40 to-teal-600/30 backdrop-blur-xl border-2 border-emerald-300/40 flex items-center justify-center shadow-lg">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-emerald-400 flex items-center justify-center">
                <span className="text-emerald-950 text-xl sm:text-2xl lg:text-3xl font-black">
                  $
                </span>
              </div>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-black tracking-tight">
                Cash<span className="text-emerald-400">Tap</span>
              </h1>
              <p className="text-emerald-300/70 text-[0.65rem] sm:text-xs lg:text-sm font-medium tracking-wider mt-0.5">
                USD WALLET
              </p>
            </div>
          </div>
          <div className="mt-7 md:ml-0 ml-20 sm:mt-12 lg:mt-14">
            <div className="flex items-end gap-2 sm:gap-3">
              <p className="text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter leading-none">
                {loadingBalance ? (
                  <span className="text-white/40">••••••</span>
                ) : (
                  formatAmount(parseFloat(walletBalance))
                )}
              </p>
              <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-300 pb-0.5">
                USD
              </span>
            </div>
            <p className="text-emerald-200/80 text-xs sm:text-sm font-medium mt-1.5">
              Available Balance
            </p>
          </div>
          {/* CashTap Tag + Debit Card */}
          <div className="mt-4 sm:mt-6 lg:mt-5 rounded-xl border border-emerald-400/20 bg-emerald-900/30 backdrop-blur-sm p-4 sm:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-emerald-200/60 text-[0.65rem] sm:text-xs font-semibold tracking-widest uppercase mb-2">
                  CashTap Tag
                </p>
                {editingTag ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xl sm:text-2xl font-black text-emerald-400">$</span>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value.toLowerCase().replace(/\s/g, ""))}
                      onKeyDown={(e) => e.key === "Enter" && saveTag()}
                      autoFocus
                      maxLength={15}
                      className="bg-emerald-900/50 border border-emerald-400/30 rounded-lg px-2 py-1 text-lg sm:text-xl font-bold text-white outline-none focus:border-emerald-400/60 w-36 sm:w-44"
                    />
                    <button onClick={saveTag} disabled={savingTag}
                      className="p-1.5 bg-emerald-500/20 border border-emerald-400/30 rounded-lg text-emerald-400 hover:bg-emerald-500/30 transition-colors">
                      {savingTag ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setEditingTag(false)}
                      className="p-1.5 bg-gray-500/20 border border-gray-500/30 rounded-lg text-gray-400 hover:bg-gray-500/30 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight leading-none flex items-center gap-2">
                    <span>
                      <span className="text-emerald-400">$</span>
                      {username || "user"}
                    </span>
                    <button onClick={() => { setTagInput(user?.username || ""); setEditingTag(true); }}
                      className="p-1 text-emerald-400/50 hover:text-emerald-400 transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {user?.isBusiness && (
                      <span className="ml-1 px-2 py-0.5 text-[10px] sm:text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/25 rounded-full inline-flex items-center gap-1 align-middle">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Business
                      </span>
                    )}
                  </p>
                )}
                <p className="text-[0.6rem] sm:text-xs text-emerald-300/50 font-medium mt-2">
                  Member since{" "}
                  {user?.joinedAt
                    ? new Date(user.joinedAt).toLocaleDateString("en-US", {
                        month: "short",
                        year: "2-digit",
                      })
                    : "Oct '25"}
                </p>
              </div>
              {/* Debit Card Badge */}
              <div className="bg-gradient-to-r from-emerald-500/20 to-teal-600/20 backdrop-blur-xl px-3 py-2 sm:px-4 sm:py-2.5 rounded-full border border-emerald-400/30 shadow-md flex justify-center mt-5 items-center gap-2">
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-sm">
                  <span className="text-emerald-950 text-sm sm:text-base font-black">$</span>
                </div>
                <div className="flex flex-col items-start">
                  <p className="text-emerald-300 font-bold text-[0.6rem] sm:text-[0.65rem] tracking-widest leading-tight uppercase">
                    {(username || "user").toUpperCase()}
                  </p>
                  <p className="text-emerald-400/80 text-[0.5rem] sm:text-[0.55rem] font-semibold leading-tight">
                    DEBIT CARD
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bank & Money Transfer Section */}
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/40 rounded-3xl p-3 sm:p-7 lg:p-8 backdrop-blur-md shadow-xl">
        <h3 className="text-lg md:text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <img
            src="./Flag_of_the_United_States.png"
            className="text-emerald-400 h-full w-10"
          />
          U.S. Bank Account
        </h3>
        <div className="flex flex-col md:flex-row justify-between space-x-0 md:space-y-0 space-y-5 md:space-x-5">
          {/* Add Money Card (On-Ramp side) */}
          <div className="bg-gray-900/45 h-fit w-full md:w-1/2 border border-gray-700/60 rounded-2xl p-2 sm:p-6 transition-all duration-300 hover:border-emerald-500/40 hover:shadow-emerald-500/10 group">
            {loadingBridge ? (
              <div className="flex justify-center py-10">
                <GridLoader color="#61CE70" size={11} />
              </div>
            ) : !customerId ? (
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-900/50 to-amber-950/50 border border-amber-800/60 rounded-full text-amber-300/90 text-[0.6rem] md:text-sm font-medium backdrop-blur-sm shadow-sm">
                  <Clock size={16} className="text-amber-400" />
                  <span>Create U.S. Bank Account</span>
                </div>
                <div className="space-y-2">
                  <h5 className="text-lg font-semibold text-white tracking-tight">
                    Get your personal US bank details
                  </h5>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Receive ACH transfers, wires, and other bank payments
                    directly into your CashTap wallet.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/setup-usbank")}
                  className={`
                    group relative w-full py-4 px-6
                    bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600
                    hover:from-emerald-500 hover:via-emerald-400 hover:to-teal-500
                    text-white font-semibold text-base
                    rounded-2xl shadow-lg shadow-emerald-900/30
                    hover:shadow-emerald-600/40 hover:shadow-xl
                    transition-all duration-300
                    flex items-center justify-center gap-2.5
                    active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-emerald-400/50
                  `}
                >
                  <Plus
                    size={18}
                    className="group-hover:scale-110 transition-transform"
                  />
                  <span>Get My US Account</span>
                </button>
                <div className="flex items-start gap-2.5 pt-1">
                  <div className="mt-0.5 text-emerald-400/80">
                    <LockIcon size={18} />
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Most users complete setup in just a few minutes.
                    <br />
                    <span className="text-gray-400">
                      Secure identity verification is required (handled by
                      CashTap’s regulated banking partners).
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => setShowKycInfoModal(true)}
                  className="text-emerald-400/80 hover:text-emerald-300 text-xs font-medium flex items-center gap-1.5 mt-1 transition-colors"
                >
                  Why do I need to verify my identity? →
                </button>
              </div>
            ) : !termsAccepted ? (
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-950/60 border border-yellow-800/50 rounded-full text-yellow-300 text-sm font-medium">
                  <AlertCircle size={16} />
                  Accept Terms
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Accept the service terms to continue setup.
                </p>
                <button
                  onClick={() => navigate("/setup-usbank")}
                  className="w-full py-4 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold rounded-2xl shadow-md hover:shadow-emerald-500/30 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  <Shield size={18} />
                  Accept Terms
                </button>
                <div className="flex items-start gap-2.5 pt-1">
                  <div className="mt-0.5 text-emerald-400/80">
                    <LockIcon size={18} />
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Most users complete setup in just a few minutes.
                    <br />
                    <span className="text-gray-400">
                      Secure identity verification is required (handled by
                      CashTap’s regulated banking partners).
                    </span>
                  </p>
                </div>
              </div>
            ) : kycState !== "approved" ? (
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-950/60 border border-amber-800/50 rounded-full text-amber-300 text-sm font-medium">
                  <Clock size={16} />
                  {kycState === "under_review"
                    ? "KYC Under Review"
                    : "Complete KYC"}
                </div>
                <p className="text-gray-300 min-h-12 text-sm leading-relaxed">
                  {kycState === "under_review"
                    ? "Your identity verification is being processed. This usually takes 1–3 business days."
                    : "Verify your identity to enable adding money via bank transfer."}
                </p>
                <button
                  onClick={() => navigate("/setup-usbank")}
                  className="w-full py-4 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold rounded-2xl shadow-md hover:shadow-emerald-500/30 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  <UserCheck size={18} />
                  {kycState === "under_review"
                    ? "Check Status"
                    : "Complete KYC"}
                </button>
              </div>
            ) : quotes.length > 0 || externalAccounts.length > 0 ? (
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 px-2 md:px-3.5 py-1.5 bg-emerald-950/60 border border-emerald-800/40 rounded-full text-emerald-300 text-[0.6rem] md:text-sm font-medium backdrop-blur-sm shadow-sm">
                  <CheckCircle size={16} className="text-emerald-400" />
                  U.S. Bank Account Connected
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Your U.S. bank account is active and ready to receive deposits
                  via ACH.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <button
                    onClick={() => setShowBankModal(true)}
                    className="py-3 px-5 bg-emerald-950/40 hover:bg-emerald-900/50 border border-emerald-700/40 text-emerald-300 font-medium rounded-xl transition-all flex items-center justify-center gap-2 text-sm active:scale-[0.98]"
                  >
                    <Banknote size={17} />
                    View Details
                  </button>
                  <button
                    onClick={() => navigate("/history")}
                    className="py-3 px-5 bg-gray-800/70 hover:bg-gray-700/70 text-gray-200 font-medium rounded-xl transition-all flex items-center justify-center gap-2 text-sm active:scale-[0.98]"
                  >
                    <ArrowRightCircle size={17} />
                    Transfer History
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-5 pt-1">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-950/60 border border-emerald-800/40 rounded-full text-emerald-300 text-sm font-medium">
                  <CheckCircle size={16} className="text-emerald-400" />
                  KYC Approved
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  One final step: generate your personal US bank account.
                </p>
                <button
                  onClick={() => navigate("/setup-usbank")}
                  className="w-full py-4 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold rounded-2xl shadow-md hover:shadow-emerald-500/30 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  <Plus size={18} />
                  Get US Bank Details
                </button>
                <div className="flex items-start gap-2.5 pt-1">
                  <div className="mt-0.5 text-emerald-400/80">
                    <LockIcon size={18} />
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Most users complete setup in just a few minutes.
                    <br />
                    <span className="text-gray-400">
                      Secure identity verification is required (handled by
                      CashTap’s regulated banking partners).
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => navigate("/setup-usbank")}
                  className="text-emerald-400/80 hover:text-emerald-300 text-xs font-medium flex items-center gap-1.5 mt-1 transition-colors"
                >
                  Why do I need to verify my identity? →
                </button>
              </div>
            )}
          </div>

          {/* Debit Card – Coming Soon */}
          <div className="group relative flex min-h-[400px] w-full md:w-1/2 flex-col overflow-hidden rounded-2xl border border-gray-700/60 bg-gray-900/45 p-6 transition-all duration-300 hover:border-amber-500/30 hover:shadow-amber-500/10 sm:p-8 md:max-w-2xl">
            <div className="pointer-events-none absolute inset-0 z-10 rounded-2xl bg-black/40 backdrop-blur-[1.5px]" />
            <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
              <div className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 backdrop-blur-md shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] sm:px-4">
                <div className="mr-2 h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-white/90 sm:text-xs">
                  Coming Soon
                </span>
              </div>
            </div>
            <div className="relative z-10 flex flex-1 flex-col justify-between py-4">
              <div className="relative mx-auto mb-8 aspect-[1.58/1] w-full max-w-[260px] transition-transform duration-500 group-hover:scale-[1.02] sm:max-w-[320px]">
                <div className="absolute inset-0 rounded-2xl border border-gray-700/70 bg-gradient-to-br from-gray-800 via-gray-900 to-black shadow-2xl">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50" />
                  <div className="absolute left-4 top-4 h-7 w-10 flex items-center justify-center rounded-md bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 shadow-lg sm:left-6 sm:top-6 sm:h-9 sm:w-12 sm:rounded-lg">
                    <div className="grid grid-cols-4 gap-0.5">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-1.5 w-1 bg-amber-700/60 rounded-full sm:h-2 sm:w-1.5"
                        />
                      ))}
                    </div>
                  </div>
                  <div className="absolute right-4 top-4 rounded-lg border border-white/20 bg-white/10 p-1.5 backdrop-blur-md sm:right-6 sm:top-6 sm:p-2">
                    <Nfc className="h-4 w-4 text-white/80 sm:h-5 sm:w-5" />
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between sm:bottom-6 sm:left-6 sm:right-6">
                    <div className="flex items-center gap-2 sm:gap-2.5">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 shadow-md sm:h-8 sm:w-8">
                        <span className="text-sm font-black text-emerald-950 sm:text-lg">
                          $
                        </span>
                      </div>
                      <span className="text-sm font-bold tracking-wide text-white/90 sm:text-lg">
                        Cash<span className="text-emerald-400">Tap</span>
                      </span>
                    </div>
                    <div className="flex -space-x-2">
                      <div className="h-6 w-6 rounded-full border-2 border-white/40 bg-orange-500/80 sm:h-8 sm:w-8" />
                      <div className="h-6 w-6 rounded-full border-2 border-white/40 bg-red-500/80 sm:h-8 sm:w-8" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-auto max-w-sm space-y-4 text-center">
                <h4 className="text-xl font-bold tracking-tight text-white sm:text-2xl lg:text-3xl">
                  Get Your Card
                </h4>
                <p className="text-sm leading-relaxed text-gray-300 sm:text-base">
                  Set up a card to start using your available funds for everyday
                  payments.
                </p>
                <div className="mt-2 select-none rounded-xl border border-gray-700 bg-gray-800/60 px-6 py-3 text-sm font-medium text-gray-400 cursor-not-allowed sm:px-8 sm:py-4 sm:text-base">
                  Set Up Card
                </div>
                <p className="text-xs text-gray-500 sm:text-sm">
                  Card availability will be announced soon
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-col-1 md:grid-cols-3 gap-2 sm:gap-4 mb-8 sm:mb-10">
        <button
          onClick={() => navigate("/add-funds")}
          className={`
            relative overflow-hidden
            bg-gradient-to-br from-[#0F1419] via-[#141a1f] to-[#1A1F2B]
            border border-gray-700/40
            rounded-2xl
            p-5 sm:p-6
            text-white
            transition-all duration-300
            hover:border-[#61CE70]/50
            hover:shadow-xl hover:shadow-[#61CE70]/20
            hover:scale-[1.03]
            active:scale-[0.98]
            group
          `}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-25 transition-opacity duration-500 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-[#61CE70]/10 via-transparent to-[#61CE70]/5 animate-pulse-slow"></div>
          </div>
          <div className="relative flex flex-col items-center space-y-3 sm:space-y-4 z-10">
            <div
              className={`
                p-3.5
                bg-gradient-to-br from-[#61CE70]/30 to-[#4CAF50]/20
                rounded-xl
                border border-[#61CE70]/25
                group-hover:scale-110 group-hover:rotate-3
                transition-all duration-300
                shadow-sm shadow-[#61CE70]/30
              `}
            >
              <PlusIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#61CE70]" />
            </div>
            <span className="font-semibold text-sm sm:text-base tracking-tight">
              Add Money
            </span>
            <p
              className={`
                text-xs sm:text-sm
                text-gray-400/90 font-light leading-relaxed text-center
                max-w-[240px] sm:max-w-none
                group-hover:text-gray-300/95
                transition-colors duration-300
              `}
            >
              Add funds to your{" "}
              <strong className="text-[#81E68A] font-medium">
                CashTap wallet
              </strong>
              <br />
              from bank accounts or cards
            </p>
          </div>
        </button>

        <button
          onClick={() => navigate("/transfer")}
          className={`
            relative overflow-hidden
            bg-gradient-to-br from-[#0F1419] via-[#141a22] to-[#1A1F2B]
            border border-gray-700/40
            rounded-2xl
            p-5 sm:p-6
            text-white
            transition-all duration-300
            hover:border-blue-600/50
            hover:shadow-xl hover:shadow-blue-900/25
            hover:scale-[1.03]
            active:scale-[0.98]
            group
          `}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-transparent to-blue-600/5 animate-pulse-slow"></div>
          </div>
          <div className="relative flex flex-col items-center space-y-3 sm:space-y-4 z-10">
            <div
              className={`
                p-3.5
                bg-gradient-to-br from-blue-900/35 to-blue-800/20
                rounded-xl
                border border-blue-800/30
                group-hover:scale-110 group-hover:rotate-2
                transition-all duration-300
                shadow-sm shadow-blue-900/30
              `}
            >
              <Send className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400" />
            </div>
            <span className="font-semibold text-sm sm:text-base tracking-tight">
              Send Money
            </span>
            <p
              className={`
                text-xs sm:text-sm
                text-gray-400/90 font-light leading-relaxed text-center
                max-w-[220px] sm:max-w-none
                group-hover:text-gray-300/95
                transition-colors duration-300
              `}
            >
              To other{" "}
              <strong className="text-green-400 font-medium">CashTap</strong>{" "}
              users,
              <br />
              <strong className="text-green-400 font-medium"> wallets</strong>,
              and{" "}
              <strong className="text-green-400 font-medium">
                bank accounts
              </strong>
            </p>
          </div>
        </button>
        <button
          onClick={() => navigate("/collect")}
          className="relative overflow-hidden bg-gradient-to-br from-[#0a1210] via-[#0F1419] to-[#111a15] border border-[#61CE70]/30 rounded-2xl p-5 sm:p-7 text-white hover:border-[#61CE70]/60 hover:shadow-xl hover:shadow-[#61CE70]/15 transition-all duration-300 group hover:scale-[1.03] active:scale-[0.98] md:col-span-1"
        >
          {/* Glow dot */}
          {/* <div className="absolute top-4 left-4 w-2.5 h-2.5 rounded-full bg-[#61CE70] shadow-lg shadow-[#61CE70]/50" /> */}
          {/* Corner glow effects */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#61CE70]/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#61CE70]/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative flex flex-col items-center space-y-3 sm:space-y-4 z-10">
            {/* Icon */}
            <div className="p-4 bg-gradient-to-br from-[#61CE70]/20 to-[#61CE70]/5 rounded-full border border-[#61CE70]/20 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#61CE70]/10">
              <HandCoins className="w-7 h-7 sm:w-8 sm:h-8 text-[#61CE70]" />
            </div>

            {/* Title */}
            <span className="font-bold text-lg sm:text-xl tracking-tight">
              Collect
            </span>

            {/* Subtitle */}
            <p className="text-xs sm:text-sm text-gray-400 font-light text-center leading-relaxed">
              Create{" "}
              <strong className="text-[#61CE70] font-medium">
                Payment Links
              </strong>
              , <strong className="text-[#61CE70] font-medium">Invoices</strong>
              ,{" "}
              <strong className="text-[#61CE70] font-medium">Donations</strong>{" "}
              & <strong className="text-[#61CE70] font-medium">Tip Jars</strong>
            </p>

            {/* Divider */}
            <div className="w-full border-t border-gray-700/40" />

            {/* Feature pills */}
            <div className="grid grid-cols-2 gap-2">
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300">
                <Link2 className="w-3.5 h-3.5 text-[#61CE70]" />
                Payment Links
              </span>

              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300">
                <FileText className="w-3.5 h-3.5 text-[#61CE70]" />
                Invoices
              </span>

              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300">
                <Heart className="w-3.5 h-3.5 text-[#61CE70]" />
                Donations
              </span>

              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300">
                <Gift className="w-3.5 h-3.5 text-[#61CE70]" />
                Tips Jars
              </span>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate("/paymentRequests")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-purple-500/20 rounded-xl group-hover:bg-purple-500/30 transition-colors">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">
              Payment Request
            </span>
          </div>
        </button>

        <button
          onClick={() => navigate("/wallet")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-blue-500/20 rounded-xl group-hover:bg-blue-500/30 transition-colors">
              <Wallet className="w-5 h-5 text-blue-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">View Wallet</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/analytics")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-orange-500/50 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-orange-500/20 rounded-xl group-hover:bg-orange-500/30 transition-colors">
              <TimerIcon className="w-5 h-5 text-orange-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Analytics</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/earn")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-yellow-500/50 hover:shadow-lg hover:shadow-yellow-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-yellow-500/20 rounded-xl group-hover:bg-yellow-500/30 transition-colors">
              <TrendingUp className="w-5 h-5 text-yellow-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Earn</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/shopping")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-pink-500/50 hover:shadow-lg hover:shadow-pink-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-pink-500/20 rounded-xl group-hover:bg-pink-500/30 transition-colors">
              <Users className="w-5 h-5 text-pink-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">Shopping</span>
          </div>
        </button>

        <button
          onClick={() => navigate("/history")}
          className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-5 sm:p-6 text-white hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 group hover:scale-105"
        >
          <div className="flex flex-col items-center space-y-2 sm:space-y-3">
            <div className="p-3 bg-purple-500/20 rounded-xl group-hover:bg-purple-500/30 transition-colors">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <span className="font-medium text-xs sm:text-sm">History</span>
          </div>
        </button>
      </div>

      {/* Payment Logos Marquee */}
      <section
        className="py-0 md:py-16 overflow-hidden"
        aria-label="Trusted payment partners"
      >
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
          <div className="text-center mb-5 md:mb-12">
            <h3 className="inline-block text-3xl sm:text-5xl font-black tracking-tight bg-gradient-to-r from-blue-500 via-cyan-400 to-purple-600 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient-shift">
              Powered By
            </h3>
          </div>
          <div className="relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden">
            <div className="py-10">
              <div className="flex animate-marquee gap-5 md:gap-20">
                {[...paymentLogos, ...paymentLogos].map((logo, i) => (
                  <div key={i} className="flex-shrink-0">
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className={`h-5 md:h-12 ${logo.scale} object-contain transition-all duration-300 hover:scale-125 hover:drop-shadow-2xl cursor-pointer ${
                        logo.alt === "Crossmint" ||
                        logo.alt === "Mastercard" ||
                        logo.alt === "Bridge" ||
                        logo.alt === "Yield"
                          ? "bg-white p-1 rounded-lg"
                          : ""
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0F1419] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#0F1419] to-transparent" />
          </div>
        </div>
        <style>{`
          @keyframes marquee {
            from { transform: translateX(0); }
            to { transform: translateX(-100%); }
          }
          .animate-marquee {
            animation: marquee 35s linear infinite;
          }
          .animate-marquee:hover {
            animation-play-state: paused;
          }
          @keyframes gradient-shift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradient-shift 6s ease infinite;
          }
        `}</style>
      </section>

      {/* Recent Activity + Account Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">
              Recent Transactions
            </h3>
            <button
              onClick={() => setShowAllTransactions(true)}
              className="text-[#61CE70] hover:text-[#50B060] text-sm font-medium transition-colors"
            >
              View All
            </button>
          </div>

          {loadingTransactions ? (
            <div className="text-center text-gray-400">
              Loading transactions...
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center text-gray-400">
              No transactions found.
            </div>
          ) : (
            <ul className="divide-y divide-gray-800/50 -mx-3 sm:-mx-4">
              {transactions.slice(0, 2).map((tx) => {
                const isReceived = tx.type === "received";
                const isHeld = tx.status === "held";
                const isAwaiting = tx.status === "awaiting_approval";
                const isRejected = tx.status === "rejected";
                const isReleaseFailed = tx.status === "release_failed";
                const photo = getCounterpartyPhoto(tx);
                const counterpartyAddr = isReceived ? tx.from : tx.to;
                const accent = isRejected
                  ? "text-gray-500"
                  : isHeld || isAwaiting || isReleaseFailed
                  ? "text-amber-400"
                  : isReceived
                    ? "text-[#61CE70]"
                    : "text-red-400";
                const accentBg = isRejected
                  ? "bg-gray-500/10"
                  : isHeld || isAwaiting || isReleaseFailed
                  ? "bg-amber-500/10"
                  : isReceived
                    ? "bg-[#61CE70]/10"
                    : "bg-red-500/10";

                return (
                  <li
                    key={tx.id}
                    className="group px-3 sm:px-4 py-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar with type indicator */}
                      <div className="relative shrink-0">
                        <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-800 border border-gray-700/60 flex items-center justify-center">
                          {photo ? (
                            <img
                              src={photo}
                              alt={getCounterpartyDisplay(tx)}
                              className="w-full h-full object-cover"
                              onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                            />
                          ) : counterpartyAddr.toLowerCase() === adminWallet ? (
                            <Shield className="w-5 h-5 text-gray-500" />
                          ) : (
                            <Users className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <div
                          className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full ${accentBg} ring-2 ring-[#0F1419] flex items-center justify-center`}
                          aria-label={isReceived ? "Received" : "Sent"}
                        >
                          {isReceived ? (
                            <ArrowDownLeft className={`w-3 h-3 ${accent}`} />
                          ) : (
                            <ArrowUpRight className={`w-3 h-3 ${accent}`} />
                          )}
                        </div>
                      </div>

                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-base sm:text-lg font-bold tracking-tight truncate">
                            {tx.sourceLabel || (isReceived ? "Deposit" : "Transfer")}
                          </span>
                          {tx.paymentMethod && (
                            <span className="text-[10px] uppercase tracking-wide font-medium text-gray-400 bg-gray-800/70 px-1.5 py-0.5 rounded-md">
                              {tx.paymentMethod}
                            </span>
                          )}
                          {isHeld && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                              title={tx.flagReason || "Funds held pending manual review"}
                            >
                              Held
                            </span>
                          )}
                          {isAwaiting && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                              title="Funds received — waiting for approval"
                            >
                              <Clock className="w-3 h-3" />
                              Awaiting approval
                            </span>
                          )}
                          {isReleaseFailed && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                              title="Your deposit is being processed — contact support if this persists."
                            >
                              <Clock className="w-3 h-3" />
                              Processing
                            </span>
                          )}
                          {isRejected && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-red-300 bg-red-500/15 border border-red-500/30 px-1.5 py-0.5 rounded-md"
                              title="This deposit was not approved — contact support"
                            >
                              Not approved
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-1">
                          {isReceived ? "From" : "To"}{" "}
                          <span className="text-gray-200 font-medium">{getCounterpartyDisplay(tx)}</span>
                        </p>
                        {isHeld && (
                          <p className="text-[11px] text-amber-200/80 mt-1 leading-snug">
                            Pending review — received ${formatNetAmount(tx.grossAmount ?? tx.amount)} of expected ${formatNetAmount(tx.expectedAmount ?? 0)}
                            {tx.feeAmount != null && tx.feeAmount > 0 && (
                              <> · Net on release ${formatNetAmount(tx.amount)} (after ${formatFee(tx.feeAmount)} fee)</>
                            )}
                          </p>
                        )}
                        {isAwaiting && (
                          <p className="text-[11px] text-amber-200/80 mt-1 leading-snug">
                            Funds received — your deposit is being reviewed and will be added to your balance once approved.
                          </p>
                        )}
                        {isRejected && (
                          <p className="text-[11px] text-red-200/80 mt-1 leading-snug">
                            This deposit was not approved. Contact support@cashtap.cash for details.
                          </p>
                        )}
                        <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1">
                          <span>{tx.date}</span>
                          <span className="text-gray-700">•</span>
                          <button
                            onClick={() => copyToClipboard(tx.hash)}
                            className="inline-flex items-center gap-1 hover:text-gray-300 transition-colors"
                            title="Copy tx hash"
                          >
                            {truncateAddress(tx.hash)}
                            <Copy className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => viewOnBlockExplorer(tx.hash)}
                            className="inline-flex items-center gap-1 hover:text-[#61CE70] transition-colors"
                            title="View on BaseScan"
                          >
                            Explorer
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      {/* Amount + inline fee */}
                      <div className="shrink-0 text-right">
                        <div className="flex items-baseline justify-end gap-2">
                          <span className={`text-base sm:text-lg font-bold tabular-nums ${accent}`}>
                            {isReceived
                              ? `+${formatNetAmount(tx.amount)}`
                              : `−${formatAmount(tx.amount)}`}
                          </span>
                          {isReceived && tx.feeAmount != null && tx.feeAmount > 0 && (
                            <span className="text-[11px] text-red-400 font-semibold tabular-nums whitespace-nowrap">
                              <span className="text-red-400/40 mx-0.5">·</span>
                              −${formatFee(tx.feeAmount)} fee
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">
                          USD
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 sm:p-8 w-full sm:max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white">
                Withdraw Amount
              </h3>
              <button
                onClick={() => {
                  setShowWithdraw(false);
                  setWithdrawAmount("");
                }}
                className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-6">
              <div>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Enter amount in USD"
                  className="w-full p-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 text-2xl font-bold text-center focus:outline-none focus:border-[#61CE70]/50 transition"
                />
                <p className="text-center text-gray-400 text-sm mt-3">
                  Available: {formatAmount(parseFloat(walletBalance))} USD
                </p>
              </div>
              <button
                onClick={handleWithdraw}
                disabled={isWithdrawing}
                className="w-full bg-[#61CE70] hover:bg-[#50B060] disabled:opacity-70 text-white font-bold py-5 rounded-2xl text-lg transition duration-300 shadow-lg"
              >
                {isWithdrawing ? "Opening Coinbase..." : "Continue in Coinbase"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Account Modal */}
      {showBankModal && quotes.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 sm:p-8 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#0F1419] z-10 pb-2">
              <h3 className="text-sm md:text-xl font-bold text-white">
                U.S. Bank Account Details
              </h3>
              <button
                onClick={() => setShowBankModal(false)}
                className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <div className="space-y-5 pb-4">
              <div className="p-5 bg-gray-800/50 rounded-2xl border border-gray-700/70">
                <Field
                  label="Bank Name"
                  value={quotes[0].source_deposit_instructions.bank_name}
                />
                <Field
                  label="Account Holder / Beneficiary"
                  value={
                    quotes[0].source_deposit_instructions.bank_beneficiary_name
                  }
                />
                <Field
                  label="Account Number"
                  value={
                    quotes[0].source_deposit_instructions.bank_account_number
                  }
                />
                <Field
                  label="Routing Number"
                  value={
                    quotes[0].source_deposit_instructions.bank_routing_number
                  }
                />
                <Field
                  label="Bank Address"
                  value={quotes[0].source_deposit_instructions.bank_address}
                />
                <Field
                  label="Beneficiary Address"
                  value={
                    quotes[0].source_deposit_instructions
                      .bank_beneficiary_address
                  }
                />
                <Field
                  label="Currency"
                  value={quotes[0].source_deposit_instructions.currency.toUpperCase()}
                />
              </div>
              <div className="text-xs text-gray-500 text-center pt-3">
                Quote ID: {quotes[0].id.slice(0, 8)}... • Created{" "}
                {new Date(quotes[0].created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Address Modal */}
      {showAddressModal && liquidationAddresses.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
          <div
            className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B]
              border border-gray-700/50
              rounded-3xl
              p-6 sm:p-8
              w-full sm:max-w-lg
              max-h-[90vh]
              overflow-y-auto
              scrollbar-thin
              scrollbar-track-gray-900
              scrollbar-thumb-gray-700
              hover:scrollbar-thumb-gray-600"
          >
            <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#0F1419] z-10">
              <h3 className="text-xl font-bold text-white">
                Off-Ramp Deposit Address
              </h3>
              <button
                onClick={() => setShowAddressModal(false)}
                className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <div className="space-y-5">
              <div className="p-5 bg-gray-800/50 rounded-2xl border border-gray-700/70">
                <p className="text-gray-400 text-sm mb-1">
                  USDC Deposit Address (Base)
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-white font-mono text-sm break-all">
                    {liquidationAddresses[0].address}
                  </p>
                  <button
                    onClick={() =>
                      copyToClipboard(liquidationAddresses[0].address)
                    }
                    className="p-2 hover:bg-gray-700 rounded-lg transition"
                  >
                    <Copy className="w-5 h-5 text-gray-400 hover:text-white" />
                  </button>
                </div>
              </div>
              <div className="p-5 bg-gray-800/50 rounded-2xl border border-gray-700/70">
                <p className="text-gray-400 text-sm mb-1">Status</p>
                <p className="text-[#61CE70] font-medium">
                  {liquidationAddresses[0].state}
                </p>
              </div>
              <div className="p-5 bg-gray-800/50 rounded-2xl border border-gray-700/70">
                <p className="text-gray-400 text-sm mb-1">Destination</p>
                <p className="text-white font-medium">
                  USD via{" "}
                  {liquidationAddresses[0].destination_payment_rail.toUpperCase()}
                </p>
              </div>
              <div className="p-5 bg-gray-800/50 rounded-2xl border border-gray-700/70">
                <p className="text-gray-400 text-sm mb-1">Created</p>
                <p className="text-white font-medium">
                  {new Date(
                    liquidationAddresses[0].created_at,
                  ).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => navigate("/link-bank")}
                className="w-full py-4 mt-4 bg-red-600 hover:bg-red-500 text-white font-bold rounded-2xl transition flex items-center justify-center gap-2 shadow-lg"
              >
                <ArrowRightCircle size={20} />
                View Off-Ramp Details & Activity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Transactions Modal */}
      {showAllTransactions && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-6 sm:p-8 w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#0F1419] z-10">
              <h3 className="text-lg sm:text-xl font-bold text-white">
                All Transactions
              </h3>
              <button
                onClick={() => setShowAllTransactions(false)}
                className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {transactions.length === 0 ? (
              <div className="text-center text-gray-400 py-6">
                No transactions found.
              </div>
            ) : (
              <ul className="divide-y divide-gray-800/50 -mx-2 sm:-mx-4">
                {transactions.map((tx) => {
                  const isReceived = tx.type === "received";
                  const isHeld = tx.status === "held";
                  const isAwaiting = tx.status === "awaiting_approval";
                  const isRejected = tx.status === "rejected";
                  const isReleaseFailed = tx.status === "release_failed";
                  const photo = getCounterpartyPhoto(tx);
                  const counterpartyAddr = isReceived ? tx.from : tx.to;
                  const accent = isRejected
                    ? "text-gray-500"
                    : isHeld || isAwaiting || isReleaseFailed
                    ? "text-amber-400"
                    : isReceived
                      ? "text-[#61CE70]"
                      : "text-red-400";
                  const accentBg = isRejected
                    ? "bg-gray-500/10"
                    : isHeld || isAwaiting || isReleaseFailed
                    ? "bg-amber-500/10"
                    : isReceived
                      ? "bg-[#61CE70]/10"
                      : "bg-red-500/10";

                  return (
                    <li
                      key={tx.id}
                      className="group px-2 sm:px-4 py-4 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {/* Avatar with type indicator */}
                        <div className="relative shrink-0">
                          <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-800 border border-gray-700/60 flex items-center justify-center">
                            {photo ? (
                              <img
                                src={photo}
                                alt={getCounterpartyDisplay(tx)}
                                className="w-full h-full object-cover"
                                onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                              />
                            ) : counterpartyAddr.toLowerCase() === adminWallet ? (
                              <Shield className="w-5 h-5 text-gray-500" />
                            ) : (
                              <Users className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div
                            className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full ${accentBg} ring-2 ring-[#0F1419] flex items-center justify-center`}
                            aria-label={isReceived ? "Received" : "Sent"}
                          >
                            {isReceived ? (
                              <ArrowDownLeft className={`w-3 h-3 ${accent}`} />
                            ) : (
                              <ArrowUpRight className={`w-3 h-3 ${accent}`} />
                            )}
                          </div>
                        </div>

                        {/* Body */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white text-base sm:text-lg font-bold tracking-tight truncate">
                              {tx.sourceLabel || (isReceived ? "Deposit" : "Transfer")}
                            </span>
                            {tx.paymentMethod && (
                              <span className="text-[10px] uppercase tracking-wide font-medium text-gray-400 bg-gray-800/70 px-1.5 py-0.5 rounded-md">
                                {tx.paymentMethod}
                              </span>
                            )}
                            {isHeld && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                                title={tx.flagReason || "Funds held pending manual review"}
                              >
                                Held
                              </span>
                            )}
                            {isAwaiting && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                                title="Funds received — waiting for approval"
                              >
                                <Clock className="w-3 h-3" />
                                Awaiting approval
                              </span>
                            )}
                            {isReleaseFailed && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                                title="Your deposit is being processed — contact support if this persists."
                              >
                                <Clock className="w-3 h-3" />
                                Processing
                              </span>
                            )}
                            {isRejected && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-red-300 bg-red-500/15 border border-red-500/30 px-1.5 py-0.5 rounded-md"
                                title="This deposit was not approved — contact support"
                              >
                                Not approved
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 truncate mt-1">
                            {isReceived ? "From" : "To"}{" "}
                            <span className="text-gray-200 font-medium">{getCounterpartyDisplay(tx)}</span>
                          </p>
                          {isHeld && (
                            <p className="text-[11px] text-amber-200/80 mt-1 leading-snug">
                              Pending review — received ${formatNetAmount(tx.grossAmount ?? tx.amount)} of expected ${formatNetAmount(tx.expectedAmount ?? 0)}
                              {tx.feeAmount != null && tx.feeAmount > 0 && (
                                <> · Net on release ${formatNetAmount(tx.amount)} (after ${formatFee(tx.feeAmount)} fee)</>
                              )}
                            </p>
                          )}
                          {isAwaiting && (
                            <p className="text-[11px] text-amber-200/80 mt-1 leading-snug">
                              Funds received — your deposit is being reviewed and will be added to your balance once approved.
                            </p>
                          )}
                          {isRejected && (
                            <p className="text-[11px] text-red-200/80 mt-1 leading-snug">
                              This deposit was not approved. Contact support@cashtap.cash for details.
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1">
                            <span>{tx.date}</span>
                            <span className="text-gray-700">•</span>
                            <button
                              onClick={() => copyToClipboard(tx.hash)}
                              className="inline-flex items-center gap-1 hover:text-gray-300 transition-colors"
                              title="Copy tx hash"
                            >
                              {truncateAddress(tx.hash)}
                              <Copy className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => viewOnBlockExplorer(tx.hash)}
                              className="inline-flex items-center gap-1 hover:text-[#61CE70] transition-colors"
                              title="View on BaseScan"
                            >
                              Explorer
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        {/* Amount + inline fee */}
                        <div className="shrink-0 text-right">
                          <div className="flex items-baseline justify-end gap-2">
                            <span className={`text-base sm:text-lg font-bold tabular-nums ${accent}`}>
                              {isReceived
                                ? `+${formatNetAmount(tx.amount)}`
                                : `−${formatAmount(tx.amount)}`}
                            </span>
                            {isReceived && tx.feeAmount != null && tx.feeAmount > 0 && (
                              <span className="text-[11px] text-red-400 font-semibold tabular-nums whitespace-nowrap">
                                <span className="text-red-400/40 mx-0.5">·</span>
                                −${formatFee(tx.feeAmount)} fee
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">
                            USD
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
